// PostHog analytics utilities
// PostHog is loaded via GTM, so we access it from window

import { logger } from '@/utils/logger';

declare global {
  interface Window {
    posthog?: {
      identify: (distinctId: string, properties?: Record<string, unknown>) => void;
      capture: (event: string, properties?: Record<string, unknown>) => void;
      get_session_id: () => string;
    };
  }
}

// Queue for operations that happen before PostHog loads
let queuedIdentify: string | null = null;
let queuedEvents: Array<{ event: string; properties: Record<string, unknown> }> = [];
let isWaitingForPostHog = false;

function waitForPostHog(): void {
  if (isWaitingForPostHog) return;
  isWaitingForPostHog = true;

  const check = setInterval(() => {
    if (typeof window !== 'undefined' && window.posthog) {
      clearInterval(check);
      isWaitingForPostHog = false;

      // Process queued identify
      if (queuedIdentify) {
        window.posthog.identify(queuedIdentify);
        logger.info({ area: 'analytics' }, 'Queued identify executed: ' + queuedIdentify);
        queuedIdentify = null;
      }

      // Process queued events
      queuedEvents.forEach(({ event, properties }) => {
        window.posthog!.capture(event, properties);
        logger.info({ area: 'analytics' }, 'Queued event executed: ' + event);
      });
      queuedEvents = [];
    }
  }, 100);

  // Stop checking after 10 seconds
  setTimeout(() => {
    clearInterval(check);
    isWaitingForPostHog = false;
    if (queuedIdentify || queuedEvents.length > 0) {
      logger.warn({ area: 'analytics' }, 'PostHog did not load within 10 seconds, events may be lost');
    }
  }, 10000);
}

export function getSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.posthog?.get_session_id() ?? null;
}

export function identifyUser(userId: string): void {
  if (typeof window === 'undefined') return;

  if (window.posthog) {
    window.posthog.identify(userId);
    logger.info({ area: 'analytics' }, 'User identified: ' + userId);
  } else {
    logger.info({ area: 'analytics' }, 'PostHog not loaded yet, queuing identify for: ' + userId);
    queuedIdentify = userId;
    waitForPostHog();
  }
}

export function track(eventName: string, properties: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;

  if (window.posthog) {
    window.posthog.capture(eventName, properties);
    logger.info({ area: 'analytics' }, 'Event tracked: ' + eventName);
  } else {
    logger.info({ area: 'analytics' }, 'PostHog not loaded yet, queuing event: ' + eventName);
    queuedEvents.push({ event: eventName, properties });
    waitForPostHog();
  }
}
